import queryString from 'query-string';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import Error from '../components/error';
import Feedback from '../components/feedback';
import LoadImage from '../components/load-image';
import { ScoringTestAlert } from '../components/scoring-test-alert';
import { postMessage } from '../helpers/post-message';
import useEndTest from '../hooks/use-end-test';
import useInitializationObject from '../hooks/use-initialization-object';
import useLoadLearnosityItemsSDK from '../hooks/use-load-learnosity-items-sdk';
import { LearnosityPlayerEvent } from '../interfaces/learnosity-player-event';
import PlacementUrlObject from '../interfaces/placement-url';
import './end-of-level.css';
import '../styles/placement-test.scss';
import '@fontsource/baloo-2';
import '@fontsource/baloo-2/700.css';
import '@fontsource/inter';
import '@fontsource/inter/700.css';

export const PlacementTest: React.FC = () => {
  const location = useLocation();
  const urlParameters = queryString.parse(location.search) as unknown as PlacementUrlObject;
  const allowedConsecutiveFails = urlParameters.allowedConsecutiveFails
    ? parseInt(urlParameters.allowedConsecutiveFails, 10)
    : 0;

  const {
    loading: loadingInitializationObject,
    getItemsInitializationObject,
    itemsInitializationObject,
  } = useInitializationObject();
  const {
    loading: sendingResults,
    confirmTestFinalizationGetResults,
    endTestResult,
    testSubmitted,
    endTestResultError,
  } = useEndTest();
  const {
    loading: loadingLearnositySDK,
    getLearnositySDK,
    learnosityTestSubmitted,
    loadLearnosityTestError,
  } = useLoadLearnosityItemsSDK({ allowedConsecutiveFails });

  useEffect(() => {
    if (itemsInitializationObject.loaded !== null || loadingInitializationObject) {
      return;
    }
    getItemsInitializationObject(urlParameters);
  }, [
    urlParameters,
    getItemsInitializationObject,
    itemsInitializationObject.loaded,
    loadingInitializationObject,
  ]);

  useEffect(() => {
    if (!itemsInitializationObject?.result) {
      return;
    }
    getLearnositySDK(itemsInitializationObject.result);
  }, [itemsInitializationObject.result, getLearnositySDK]);

  useEffect(() => {
    if (learnosityTestSubmitted && !sendingResults && !testSubmitted) {
      confirmTestFinalizationGetResults(itemsInitializationObject, urlParameters);
    }
  }, [
    confirmTestFinalizationGetResults,
    itemsInitializationObject,
    learnosityTestSubmitted,
    testSubmitted,
    sendingResults,
    urlParameters,
  ]);

  useEffect(() => {
    if (endTestResult.status !== 'ok') {
      return;
    }
    postMessage({
      type: LearnosityPlayerEvent.TEST_SUBMITTED,
      redirectTo: endTestResult.redirectTo,
    });
  }, [endTestResult]);

  const loadingInitialization = loadingInitializationObject || loadingLearnositySDK;
  const loadingSubmission = sendingResults;

  const TestStateHandler = () => {
    if (
      itemsInitializationObject?.loaded === false ||
      endTestResultError ||
      loadLearnosityTestError
    ) {
      return <Error returnUrl={urlParameters.returnUrl} />;
    } else if (endTestResult?.status === 'nok') {
      return <Feedback endTestResult={endTestResult} />;
    } else if (loadingInitialization) {
      return <LoadImage key="load" />;
    } else if (loadingSubmission || endTestResult?.status === 'ok') {
      return <ScoringTestAlert />;
    }
    return null;
  };

  return (
    <div className="app placement-test">
      <div className="main-block">
        <TestStateHandler />
        <div
          key="sdk"
          className="learnosity-wrapper"
          style={{
            visibility: loadingInitialization || loadingSubmission ? 'hidden' : 'visible',
          }}
          hidden={!!endTestResult?.status || loadingSubmission}
        >
          <div id="learnosity_assess" />
        </div>
      </div>
    </div>
  );
};
